import React, { useEffect } from 'react';

/**
 * AdSafetyGuard
 * - Mobile-only runtime guard to mitigate intrusive popups and full-screen overlays not initiated by user gesture.
 * - Patches window.open to require a recent user gesture and enforce noopener/noreferrer.
 * - Cleans suspicious full-screen overlays appended directly to <body> (outside React #root).
 * - Periodically restores scrolling if an ad script forces overflow: hidden on <html>/<body>.
 */
const AdSafetyGuard: React.FC = () => {
  // Global dedupe for CrackRevenue "jerky" bubbles (runs on all devices)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const prune = () => {
      try {
        const nodes = Array.from(document.querySelectorAll('#jerky-im-avatar-bubble')) as HTMLElement[];
        if (nodes.length > 1) {
          nodes.slice(1).forEach((n) => { try { n.parentElement?.removeChild(n); } catch {} });
        }
      } catch {}
    };
    let mo: MutationObserver | null = null;
    try {
      mo = new MutationObserver(() => prune());
      mo.observe(document.body || document.documentElement, { childList: true, subtree: true });
    } catch {}
    let interval: number | null = null;
    let stop: number | null = null;
    try { interval = window.setInterval(() => prune(), 1000); } catch {}
    try { stop = window.setTimeout(() => { try { if (interval) window.clearInterval(interval); } catch {} }, 20000); } catch {}
    try { prune(); } catch {}
    return () => {
      try { mo?.disconnect(); } catch {}
      try { if (interval) window.clearInterval(interval); } catch {}
      try { if (stop) window.clearTimeout(stop); } catch {}
    };
  }, []);
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const isMobile = (() => {
      try { return !!window.matchMedia && window.matchMedia('(max-width: 768px)').matches; } catch { return window.innerWidth <= 768; }
    })();

    if (!isMobile) return; // Guard only on mobile

    // 1) Require recent user gesture for window.open on mobile
    let lastGestureTime = 0;
    const GESTURE_WINDOW_MS = 1500;

    const gestureHandler = () => { lastGestureTime = Date.now(); };
    const gestureEvents: Array<keyof WindowEventMap> = ['click', 'touchstart', 'keydown'];
    gestureEvents.forEach((evt) => window.addEventListener(evt, gestureHandler, { capture: true, passive: true }));

    const originalOpen = window.open;
    const patchedOpen: typeof window.open = (url?: string | URL, target?: string, features?: string) => {
      // Allow if within user gesture window; otherwise silently block
      const allowed = Date.now() - lastGestureTime <= GESTURE_WINDOW_MS;
      if (!allowed) {
        try { console.warn('[AdSafetyGuard] Blocked window.open without user gesture:', url); } catch {}
        return null;
      }
      const finalTarget = target || '_blank';
      const finalFeatures = features ? `${features},noopener,noreferrer` : 'noopener,noreferrer';
      try {
        return originalOpen.call(window, url as any, finalTarget, finalFeatures);
      } catch (e) {
        try { console.error('[AdSafetyGuard] window.open failed, blocked:', e); } catch {}
        return null;
      }
    };

    // Patch only once per page lifetime
    const alreadyPatched = (window as any).__adSafetyPatchedOpen;
    if (!alreadyPatched) {
      (window as any).__adSafetyPatchedOpen = true;
      (window as any).__adSafetyOpenOriginal = originalOpen;
      try { (window as any).open = patchedOpen; } catch {}
    }

    // 2) MutationObserver: remove suspicious full-screen overlays appended directly to body (not inside #root)
    const appRoot = document.getElementById('root');
    const isSuspiciousOverlay = (el: Element) => {
      if (appRoot && appRoot.contains(el)) return false; // ignore our own UI
      // Allowlist: overlays created by our own React portals/components
      try {
        const he = el as HTMLElement;
        if (he.getAttribute && he.getAttribute('data-safe-overlay') === '1') return false;
        if (he.getAttribute && he.getAttribute('data-owner') === 'react-portal') return false;
        if (he.id === 'crxcr2-fallback-backdrop' || he.classList.contains('crxcr2-fallback')) return false;
      } catch {}
      // Temporary allow vendor overlays when explicitly permitted by ad components
      try { if ((window as any).__adSafetyAllowOverlays) return false; } catch {}
      const cs = window.getComputedStyle(el as HTMLElement);
      if (!cs) return false;
      const pos = cs.position;
      const w = (el as HTMLElement).offsetWidth;
      const h = (el as HTMLElement).offsetHeight;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const zi = parseInt(cs.zIndex || '0', 10);
      const coversViewport = w >= vw * 0.95 && h >= vh * 0.95;
      const fixedOrSticky = pos === 'fixed' || pos === 'sticky';
      const veryHighZ = zi >= 99999; // common for aggressive overlays
      return fixedOrSticky && coversViewport && veryHighZ;
    };

    // Allowlist for iframes that need popups (trusted ad partners)
    const iframeAllowHosts: string[] = [
    ];
    const hostMatches = (url?: string | null) => {
      if (!url) return false;
      try {
        const u = new URL(url, window.location.href);
        const h = u.hostname;
        return iframeAllowHosts.some(allow => allow.startsWith('.') ? h.endsWith(allow) : h === allow);
      } catch { return false; }
    };

    const bodyObserver = new MutationObserver((mutations) => {
      for (const m of mutations) {
        m.addedNodes.forEach((n) => {
          if (n.nodeType === Node.ELEMENT_NODE) {
            const el = n as Element;
            if (isSuspiciousOverlay(el)) {
              try {
                console.warn('[AdSafetyGuard] Removing suspicious overlay', el);
                (el as HTMLElement).style.setProperty('display', 'none', 'important');
                // IMPORTANT: Do not physically remove nodes to avoid React unmount mismatch (NotFoundError)
                // Keeping the node hidden prevents intrusive behavior without breaking React's DOM assumptions.
              } catch {}
            }

            // 2b) Sanitize any iframe appended anywhere in the DOM (mobile only)
            const applyIframeSafety = (ifr: HTMLIFrameElement) => {
              try {
                const src = ifr.src || ifr.getAttribute('src') || '';
                const isAllow = hostMatches(src);
                if (!ifr.getAttribute('sandbox')) {
                  // Trusted partners: allow popups with user gesture
                  if (isAllow) {
                    ifr.setAttribute('sandbox', 'allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox');
                  } else {
                    ifr.setAttribute('sandbox', 'allow-scripts allow-same-origin');
                  }
                }
                ifr.setAttribute('referrerpolicy', 'no-referrer');
                try { (ifr as any).allow = 'autoplay; encrypted-media; picture-in-picture'; } catch {}
              } catch {}
            };
            if (el.tagName === 'IFRAME') applyIframeSafety(el as HTMLIFrameElement);
            el.querySelectorAll && el.querySelectorAll('iframe').forEach((ifr) => applyIframeSafety(ifr as HTMLIFrameElement));
          }
        });
      }
    });

    try { bodyObserver.observe(document.body, { childList: true, subtree: true }); } catch {}

    // Also sanitize any existing iframes at mount
    try { document.querySelectorAll('iframe').forEach((ifr) => {
      const i = ifr as HTMLIFrameElement;
      if (!i.getAttribute('sandbox')) i.setAttribute('sandbox', 'allow-scripts allow-same-origin');
      i.setAttribute('referrerpolicy', 'no-referrer');
    }); } catch {}

    // 3) Anchor hardening: ensure noopener/noreferrer for target=_blank links
    const anchorClickFix = (ev: Event) => {
      try {
        const path = (ev.composedPath && ev.composedPath()) || [];
        const a = path.find((n: any) => n && n.tagName === 'A') as HTMLAnchorElement | undefined;
        if (a && a.target === '_blank') {
          const rel = (a.getAttribute('rel') || '').toLowerCase();
          if (!rel.includes('noopener')) a.setAttribute('rel', (rel ? rel + ' ' : '') + 'noopener');
          if (!rel.includes('noreferrer')) a.setAttribute('rel', (a.getAttribute('rel') || '') + (rel ? ' ' : '') + 'noreferrer');
        }
      } catch {}
    };
    try { window.addEventListener('click', anchorClickFix, { capture: true, passive: true }); } catch {}

    // 4) Script injection guard: block known-malicious ad script hosts from being appended in top document on mobile
    const blockedHosts = [
      'profitableratecpm.com',
      'stale-father.com',
      'traverseseven.com',
      'static.scptp9.com',
      // 'vlmai-3.com', // allow CrackRevenue cams widget dependency
      'recordedthereby.com',
      'experttrafficcounter.com'
    ];
    const isBlocked = (src?: string | null) => !!src && blockedHosts.some(h => src.includes(h));
    const scriptObserver = new MutationObserver((mutations) => {
      for (const m of mutations) {
        m.addedNodes.forEach((n) => {
          if (n.nodeType === Node.ELEMENT_NODE) {
            const el = n as Element;
            if (el.tagName === 'SCRIPT') {
              const src = (el as HTMLScriptElement).src || el.getAttribute('src');
              if (isBlocked(src)) {
                try {
                  console.warn('[AdSafetyGuard] Blocked script from', src);
                  el.parentElement?.removeChild(el);
                } catch {}
              }
            }
            el.querySelectorAll && el.querySelectorAll('script').forEach((s) => {
              const src = (s as HTMLScriptElement).src || s.getAttribute('src');
              if (isBlocked(src)) {
                try { s.parentElement?.removeChild(s); } catch {}
              }
            });
          }
        });
      }
    });
    try { scriptObserver.observe(document.documentElement, { childList: true, subtree: true }); } catch {}

    // 5) Periodically restore overflow if blocked
    const restoreOverflow = () => {
      const html = document.documentElement as HTMLElement;
      if (html.style.overflow === 'hidden') html.style.overflow = '';
      if (document.body.style.overflow === 'hidden') document.body.style.overflow = '';
    };
    const overflowTimer = window.setInterval(restoreOverflow, 2000);

    return () => {
      try { gestureEvents.forEach((evt) => window.removeEventListener(evt, gestureHandler, { capture: true } as any)); } catch {}
      try { bodyObserver.disconnect(); } catch {}
      try { scriptObserver.disconnect(); } catch {}
      try { window.removeEventListener('click', anchorClickFix, { capture: true } as any); } catch {}
      try { window.clearInterval(overflowTimer); } catch {}
    };
  }, []);

  return null;
};

export default AdSafetyGuard;
